import { Row, Col, Table, Grid, message, TableProps } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import config from 'config';
import {
  RequestListFilterInput,
  RequestOutput,
  useGetRequestListQuery,
  Order,
  RequestListSort,
  PermissionsEnum,
} from 'gql/types';
import { TableSearchInput, Spin } from 'modules/common/components';
import { useDebounce, useFilter, useSort } from 'modules/common/hooks';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC, useEffect, useMemo } from 'react';

import { getColumns } from './columns';
import styles from './styles.module.scss';

const RequestsTable: FC = () => {
  const { debounce, clearDebounce } = useDebounce();
  const filter = useFilter<RequestListFilterInput>();
  const requestFilter = filter.getFilterObject();
  const sort = useSort<RequestListSort>();
  const breakpoints = Grid.useBreakpoint();
  const pagination = usePagination();
  const currentUser = useCurrentUser();
  const canUserProcessRequest = currentUser.isCan(
    PermissionsEnum.ApproveDisapproveRequests
  );

  const { data, loading } = useGetRequestListQuery({
    variables: {
      input: {
        skip: pagination.skip,
        take: config.PAGINATION.DEFAULT_LIMIT,
        sort: sort.options,
        filter: requestFilter,
      },
    },
    onCompleted: (result) => {
      pagination.setTotal(result.getRequestList.meta.total);
    },
    onError: (error) => {
      message.error(error.message);
    },
  });
  const requestList = data?.getRequestList.data as RequestOutput[];

  useEffect(() => clearDebounce, []);

  const handleSearch = (value: string) => {
    pagination.moveTo(1);
    const number = Number(value);
    const isNumber = !isNaN(number);

    if (isNumber) {
      filter.setFilterOption({ fieldName: 'ownerName', value: '' });
      filter.setFilterOption({ fieldName: 'number', value: number });
    } else {
      filter.setFilterOption({ fieldName: 'number', value: null });
      filter.setFilterOption({ fieldName: 'ownerName', value });
    }
  };

  const columns = useMemo(() => {
    return getColumns({
      breakpoints,
      canUserProcessRequest,
      sort: sort.options[0],
    });
  }, [breakpoints, currentUser, sort.options]);

  const onTableChange: TableProps<RequestOutput>['onChange'] = (
    _,
    __,
    sorter,
    extra
  ) => {
    if (extra.action === 'sort') {
      const tableSorter = sorter as SorterResult<RequestOutput>;
      const fieldName =
        RequestListSort[
          tableSorter.columnKey?.toString() as keyof typeof RequestListSort
        ];

      // Remove sort when order is cleared
      if (!tableSorter.order) {
        sort.removeSortOption(fieldName);
        return;
      }

      // Set sort order
      const order = tableSorter.order === 'ascend' ? Order.Asc : Order.Desc;
      if (fieldName) {
        sort.setSortOption({ fieldName, order });
      }
    }
  };

  return (
    <Spin spinning={loading}>
      <Row gutter={[0, 32]}>
        <Col span={24}>
          <TableSearchInput
            onSearchChange={debounce(handleSearch)}
            paramKeys={['number', 'ownerName']}
          />
        </Col>
        <Col span={24}>
          <Table
            rowClassName={styles.row}
            rowKey={({ id }) => id}
            columns={columns}
            dataSource={requestList}
            onChange={onTableChange}
            pagination={pagination.config}
          />
        </Col>
      </Row>
    </Spin>
  );
};

export default RequestsTable;
